<script lang="ts" setup>
definePageMeta({
  layout: 'new-post'
})

const id = Number(useRoute().params.id?.toString())
const articleBody = ref<HTMLDivElement>()

const ismd = computed(() => useRScreen().md.value)

onMounted(() => {
  useEmitter().on(
    'favoriteSuccess',
    ({ resourceId, resourceType, isFavorite }) => {
      if (resourceId === article.value?.article.id && resourceType === 0) {
        if (isFavorite) {
          article.value.favorite_count++
        } else {
          if (article.value.favorite_count > 0) {
            article.value.favorite_count--
          }
        }
      }
    }
  )
  useEmitter().on('rewardSuccess', ({ resourceId, resourceType, count }) => {
    if (
      article.value &&
      resourceId === article.value.article.id &&
      resourceType === 0
    ) {
      article.value.reward_count += count
    }
  })
})

const { data: article } = await useAsyncData(`article:${id}`, () =>
  fetchArticleById(id)
)

const showCover =
  article.value?.article.is_show_cover &&
  !article.value?.article.content.includes(article.value.article.cover)

if (!article.value) {
  throw createError({ statusCode: 404, message: '文章不见了' })
}

const imageCount = article.value.article.content.match('<img')?.length || 0
const readTime = getReadTime(
  htmlToText(article.value.article.content),
  imageCount
)

const keywords = ref(article.value.tag)

if (article) {
  const meta = [
    {
      name: 'description',
      content: parseDescription(htmlToText(article.value.article.content))
    },
    {
      name: 'keywords',
      content: keywords.value.join(',')
    }
  ]
  if (article.value.noindex) {
    meta.push({
      name: 'robots',
      content: 'noindex'
    })
  }
  useHead({
    title: parseTitle(article.value.article.title),
    link: [
      {
        rel: 'canonical',
        href: `https://www.mfuns.net/article/${id}`
      },
      {
        rel: 'alternate',
        media: 'only screen and (max-width: 640px)',
        href: `https://m.mfuns.net/article/${id}`
      }
    ],
    meta: meta
  })
}

const { $isAndroid, $isIOS } = useNuxtApp()
const isAndroid = $isAndroid()
const isIOS = $isIOS()

const { data: hotList } = useAsyncData(() => fetchHotPosts(5), {
  lazy: true,
  server: false,
  default: () => [] as Post.Info[]
})

const { data: relatedList } = useAsyncData(
  () => fetchRelatedPosts(id, 0, -1, 20),
  {
    lazy: true,
    server: false,
    default: () => [] as Post.Info[]
  }
)
</script>

<template>
  <div v-if="article" class="m-article myz-article">
    <div class="myz-article__actions">
      <client-only>
        <article-side-action v-if="ismd" :article="article" />
      </client-only>
    </div>
    <main class="m-article__main">
      <m-image-preview>
        <m-img
          v-if="showCover"
          :src="article.article.cover"
          height="368"
          nolazy
          fetchpriority="high"
          alt="封面"
          class="m-article__cover preview"
        />
        <article class="m-article__main-article">
          <meta itemprop="headline" :content="article.article.title" />
          <meta itemprop="keywords" :content="(keywords || []).join(',')" />
          <meta
            itemprop="datePublished"
            :content="dayjs(article.article.created_at).format()"
          />
          <meta
            itemprop="image"
            :content="
              resizeImgWithConfig(article.article.cover, { height: 368 })
            "
          />
          <div itemprop="author" itemscope itemtype="http://schema.org/Person">
            <meta itemprop="name" :content="article.user.name" />
            <meta
              itemprop="url"
              :content="`https://www.mfuns.net/member/${article.user.id}`"
            />
          </div>
          <div
            itemprop="publisher"
            itemscope
            itemtype="http://schema.org/Organization"
          >
            <meta itemprop="name" content="喵御宅" />
            <div
              itemprop="logo"
              itemscope
              itemtype="https://schema.org/ImageObject"
            >
              <meta
                itemprop="url"
                content="https://www.mfuns.net/imgs/logo.svg"
              />
              <meta itemprop="width" content="180" />
              <meta itemprop="height" content="180" />
            </div>
          </div>
          <header>
            <div class="m-article__info-meta">
              <span>
                <m-icon icon="lucide-chevron-right">
                  <template #start>
                    <m-link
                      link
                      :title="article.category.parent_category?.name"
                      :to="`/category/${article.category.parent_category?.id}`"
                    >
                      {{ article.category.parent_category?.name }}
                    </m-link>
                  </template>
                  <m-link
                    link
                    :to="`/category/${article.category.id}`"
                    :title="article.category.name"
                  >
                    {{ article.category.name }}
                  </m-link>
                </m-icon>
              </span>
            </div>
            <h1 class="m-article__title">{{ article.article.title }}</h1>
            <div class="m-article__info">
              <div class="m-article__info-meta">
                <a
                  :href="`/member/${article.user.id}`"
                  target="_blank"
                  rel="author"
                  class="link"
                >
                  {{ article.user.name }}
                </a>
                <time
                  :datetime="dayjs(article.article.created_at * 1000).format()"
                  :title="
                    dayjs(article.article.created_at * 1000)
                      .locale('zh-cn')
                      .format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (中国标准时间)')
                  "
                  class="time"
                >
                  {{
                    dayjs(article.article.created_at * 1000).format(
                      'YYYY-MM-DD HH:mm'
                    )
                  }}
                </time>
                <span class="m-article__info-meta-counts">
                  <m-icon icon="lucide-eye" />
                  <span>{{ article.view_count }}</span>
                </span>
                <span class="m-article__info-read-time">
                  <m-icon icon="lucide-timer" />
                  <span>{{ readTime }}分钟</span>
                </span>
                <span v-if="article.article.copyright === 2">
                  <m-icon icon="lucide-copyright" />
                  <span>原创声明，未经作者许可禁止转载</span>
                </span>
              </div>
            </div>
          </header>
          <div
            ref="articleBody"
            class="m-article__content"
            itemprop="articleBody"
            v-html="xss(setTargetBlank(article.article.content), 'article')"
          ></div>
          <div v-if="article.tag.length" class="m-article__tags">
            <m-tag v-for="tag in article.tag" :key="tag" link :label="tag" />
          </div>
        </article>
      </m-image-preview>
      <div id="comment" class="m-article__comment-list">
        <m-comment-list
          ref="mCommentListRef"
          :area-id="article.article.comment_area_id"
        />
      </div>
    </main>
    <aside class="m-article__side">
      <m-container class="m-article__author">
        <m-author :user="article.user" size="large" follow :row="2" />
      </m-container>
      <m-container title="热门内容" padding>
        <m-space vertical gap="12px">
          <m-post-card-leaderboard
            v-for="(p, i) in hotList"
            :key="p.id"
            :post="p"
            :rank="i + 1"
          />
        </m-space>
      </m-container>
      <m-container title="相关推荐" padding>
        <m-space vertical gap="12px">
          <m-post-card-leaderboard
            v-for="p in relatedList"
            :key="p.id"
            :post="p"
          />
        </m-space>
      </m-container>
    </aside>
  </div>
</template>

<style lang="scss" scoped>
@media screen and (max-width: 992px) {
  .m-article {
    background-color: #fff;
    border-radius: 10px;
    padding: 12px;
    &__title {
      font-size: 22px !important;
      margin-bottom: 8px !important;
    }
    &__main {
      padding: 0 !important;
    }
    &__side {
      display: none;
    }
  }
}
.myz-article {
  &__actions {
    display: none;
  }
}
@media screen and (min-width: 992px) {
  .myz-article {
    &__actions {
      width: 64px;
      margin-right: 20px;
      display: block;
    }
  }
}
.m-article {
  display: flex;
  max-width: 1350px;
  width: 100%;
  margin: 0 auto;
  &__content {
    word-wrap: break-word;
  }
  &__main {
    flex: 1;
    width: 0;
    border-radius: 8px;
    overflow: hidden;
    article {
      padding: 32px 64px;
      background-color: var(--x-bg);
    }
    @media screen and (max-width: 992px) {
      article {
        padding: 0px;
      }
    }
  }
  &__cover {
    width: 100%;
    height: 368px;
    object-fit: contain;
    background-color: var(--x-bg);
    display: block;
  }
  &__tags {
    margin-top: 12px;
  }
  &__title {
    margin-bottom: 16px;
    font-size: 32px;
  }
  &__info {
    margin-bottom: 20px;
    &-meta {
      font-size: 14px;
      color: var(--color-gray-600);
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      > * {
        margin-right: 16px;
        display: flex;
        align-items: center;
        .m-icon {
          margin-right: 4px;
        }
      }
    }
  }
  &__side {
    width: 350px;
    margin-left: 12px;
    > * {
      margin-bottom: 12px;
    }
  }
  &__author {
    padding: 12px;
    background-color: #fff;
  }
}
</style>
